<template>
  <el-dialog
    :visible="visible"
    title="发货周期维护"
    width="1200px"
    append-to-body
    :close-on-click-modal="false"
    :show-close="!saving && !exportLoading"
    :close-on-press-escape="!saving && !exportLoading"
    @close="$emit('update:visible', false)"
  >
    <el-alert
      title="数据范围：展示当前上市清单中没有发货周期的产品，系统先识别无发货周期的SKU，再排除SKU下已有发货周期的老产品，仅保留需要人工维护的新产品；维护后按产品发货周期汇总SKU发货周期"
      type="info"
      :closable="false"
    />

    <el-row
      type="flex"
      justify="space-between"
      align="middle"
      style="margin: 10px 0;"
    >
      <div>
        <el-button
          size="small"
          type="primary"
          :disabled="saving || exportLoading"
          @click="exportExcel2"
        >
          下载模板
        </el-button>
        <el-button
          size="small"
          type="primary"
          @click="importVisible = true"
        >
          批量导入
        </el-button>
        <el-button
          size="small"
          :disabled="saving || exportLoading || loading"
          :loading="exportLoading"
          @click="exportExcel"
        >
          导出
        </el-button>
      </div>
      <span>待维护产品：{{ pending }} 条</span>
    </el-row>

    <el-table
      v-loading="loading"
      :data="rows"
      border
      size="small"
      max-height="430"
    >
      <el-table-column
        prop="productCode"
        label="产品编码"
        width="140"
      />
      <el-table-column
        prop="productName"
        label="产品名称"
        min-width="180"
      />
      <el-table-column
        label="是否供应商直发"
        width="125"
      >
        <template slot-scope="scope">
          {{ isSupplierDirect(scope.row) ? '是' : '否' }}
        </template>
      </el-table-column>
      <el-table-column
        label="发货周期维护最小值"
        width="160"
      >
        <template #header>
          <el-tooltip
            effect="dark"
            placement="top"
            :open-delay="600"
          >
            <span>
              <span>发货周期维护最小值</span>
              <span class="el-icon-question"></span>
            </span>
            <div slot="content">
              <div style="max-width: 400px;">
                维护的产品发货周期必须≥此产品下包件中最长发货周期的值
              </div>
            </div>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          {{ periodText(scope.row.minDeliveryCycle) }}
        </template>
      </el-table-column>
      <el-table-column
        label="发货周期（天）"
        width="160"
      >
        <template slot-scope="scope">
          <el-input-number
            v-if="editing === scope.row.productCode"
            v-model="scope.row.inputDeliveryCycle"
            :disabled="saving"
            :controls="false"
            :precision="0"
            size="mini"
            style="width: 130px;"
          />
          <span v-else>
            {{ periodText(scope.row.inputDeliveryCycle) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="updatedBy"
        label="更新人"
        width="100"
      />
      <el-table-column
        prop="updatedTime"
        label="更新时间"
        width="170"
      />
      <el-table-column
        label="操作 / 原因"
        min-width="180"
      >
        <template slot-scope="scope">
          <el-button
            type="text"
            @click="editing = scope.row.productCode"
          >
            编辑
          </el-button>
          <span v-if="blocked(scope.row)">
            {{ isSupplierDirect(scope.row) ? '供应商直发不可维护' : '没有有效包件周期' }}
          </span>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="period-pagination"
      :current-page="page"
      :page-size="size"
      :total="total"
      :disabled="saving"
      layout="total, prev, pager, next"
      @current-change="changePage"
    />

    <span slot="footer">
      <el-button
        :disabled="saving || exportLoading"
        @click="$emit('update:visible', false)"
      >
        取消
      </el-button>
      <el-button
        type="primary"
        :loading="saving"
        @click="save"
      >
        保存本页修改
      </el-button>
    </span>

    <import-dialog
      v-if="importVisible"
      :visible.sync="importVisible"
      title="初始发货周期导入"
      report-name="newProductDeliveryCycleImport"
      app-code="ec-goods-new-api"
      :param-map="{ listNo }"
      @refresh="refresh"
    />
  </el-dialog>
</template>

<script>
import axios from '@/utils/axiosInstance'
import {
  clone,
  periodText,
  pageRequest,
  errorText,
  responseData
} from '../model'
import ImportDialog from './ImportDialog'

export default {
  components: {
    ImportDialog
  },

  props: {
    visible: Boolean,
    listNo: [String, Number]
  },

  data () {
    return {
      rows: [],
      original: [],
      pending: 0,
      total: 0,
      page: 1,
      size: 30,
      editing: '',
      importVisible: false,
      error: '',
      loading: false,
      saving: false,
      exportLoading: false,
      templateData: '',
      requestId: 0
    }
  },

  computed: {
    dirty () {
      return this.changes().length > 0
    }
  },

  created () {
    this.getTemplate()
    this.load()
  },

  beforeDestroy () {
    this.requestId++
  },

  methods: {
    periodText,

    changes () {
      return this.rows.filter(row => {
        const original = this.original.find(
          item => item.productCode === row.productCode
        ) || {}

        return row.inputDeliveryCycle !== original.inputDeliveryCycle
      })
    },

    isSupplierDirect (row) {
      return String(row.supplierDirectFlag) === '1'
    },

    blocked (row) {
      return (
        this.isSupplierDirect(row) ||
        !Number.isFinite(Number(row.minDeliveryCycle)) ||
        Number(row.minDeliveryCycle) <= 0
      )
    },

    async getTemplate () {
      const res = await axios.get(
        '/qu-platform-excel-api/excel/cfg/getTemplate/deliveryCycleMaintainList'
      )
      this.templateData = res.data
    },

    async exportExcel () {
      if (
        this.exportLoading ||
        this.saving ||
        this.loading ||
        !this.listNo
      ) {
        return
      }

      this.exportLoading = true

      try {
        const response = await this.$q.common.exportExcel({
          data: {
            appCode: 'ec-goods-new-api',
            paramMap: {
              listNo: this.listNo
            },
            reportName: 'newProductDeliveryCycleImport'
          }
        })

        if (
          !response ||
          response.success === false ||
          (response.code && !['200', '000000'].includes(String(response.code)))
        ) {
          throw new Error((response && response.message) || '提交导出任务失败')
        }

        this.$message.success('导出任务已提交，请在导出列表中查看')
      } catch (error) {
        this.$message.error(errorText(error))
      } finally {
        this.exportLoading = false
      }
    },
    async exportExcel2 () {
      if (
        this.exportLoading ||
        this.saving ||
        this.loading ||
        !this.listNo
      ) {
        return
      }

      this.exportLoading = true

      try {
        const response = await this.$q.common.exportExcel({
          data: {
            appCode: 'ec-goods-new-api',
            paramMap: {
              listNo: this.listNo
            },
            reportName: 'deliveryCycleMaintainList'
          }
        })

        if (
          !response ||
          response.success === false ||
          (response.code && !['200', '000000'].includes(String(response.code)))
        ) {
          throw new Error((response && response.message) || '提交导出任务失败')
        }

        this.$message.success('导出任务已提交，请在导出列表中查看')
      } catch (error) {
        this.$message.error(errorText(error))
      } finally {
        this.exportLoading = false
      }
    },

    downLoadTemplate () {
      const loading = this.$loading({
        lock: true,
        text: 'Loading',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      setTimeout(() => {
        this.downLoad(
          this.templateData.templatePath,
          `${this.templateData.fileName}.XLSX`
        )
        loading.close()
      }, 300)
    },

    downLoad (path, name) {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', path, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        if (xhr.status === 200) {
          if (window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveBlob(xhr.response, name)
          } else {
            const link = document.createElement('a')
            const body = document.querySelector('body')
            link.href = window.URL.createObjectURL(xhr.response)
            link.download = name
            link.style.display = 'none'
            body.appendChild(link)
            link.click()
            body.removeChild(link)
            window.URL.revokeObjectURL(link.href)
          }
        }
      }
      xhr.send()
    },

    refresh () {
      this.load()
      this.$emit('success')
    },

    changePage (page) {
      if (this.dirty) {
        this.$message.warning('请先保存或取消当前页的修改')
        return
      }

      this.page = page
      this.load()
    },

    async load () {
      const id = ++this.requestId
      this.loading = true
      this.error = ''
      this.editing = ''

      try {
        if (!this.listNo) {
          throw new Error('缺少上市清单编号')
        }

        const data = responseData(
          await this.$q.shippingPeriod.listing({
            data: pageRequest(this.page, this.size, { listNo: this.listNo })
          })
        )

        if (id === this.requestId) {
          this.rows = (data.page && data.page.list) || []
          this.original = clone(this.rows)
          this.pending = data.waitMaintainCount || 0
          this.total = Number(data.page && data.page.total) || 0
        }
      } catch (error) {
        if (id === this.requestId) {
          this.rows = []
          this.original = []
          this.total = 0
          this.pending = 0
          this.error = errorText(error)
        }
      } finally {
        if (id === this.requestId) {
          this.loading = false
        }
      }
    },

    async save () {
      console.log(1)
      if (this.loading) {
        return
      }
      console.log(2)

      const changes = this.changes()
      // if (!changes.length) {
      //   return
      // }
      console.log(3)

      // const invalid = changes.find(row => (
      //   this.blocked(row) ||
      //   !Number.isSafeInteger(Number(row.inputDeliveryCycle)) ||
      //   Number(row.inputDeliveryCycle) <= 0 ||
      //   Number(row.inputDeliveryCycle) < Number(row.minDeliveryCycle)
      // ))
      // console.log(4)

      // if (invalid) {
      //   this.error = `${invalid.productCode}：周期须为正整数且不小于维护最小值，供应商直发不可覆盖`
      //   return
      // }
      console.log(5)

      this.saving = true
      this.error = ''
      let saved = 0
console.log(123)
      try {
        for (const row of changes) {
          responseData(
            await this.$q.shippingPeriod.editListing({
              data: {
                listNo: this.listNo,
                productCode: row.productCode,
                inputDeliveryCycle: String(row.inputDeliveryCycle)
              }
            })
          )

          this.original = this.original.map(item => (
            item.productCode === row.productCode
              ? {
                ...item,
                inputDeliveryCycle: row.inputDeliveryCycle
              }
              : item
          ))
          saved++
        }

        this.$message.success(`已保存 ${saved} 条修改`)
        this.$emit('success')
        this.$emit('update:visible', false)
      } catch (error) {
        this.error = `已保存 ${saved} 条，其余修改尚未保存：${errorText(error)}`
        if (saved) {
          this.$emit('success')
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
